import { useSuspenseQuery } from '@apollo/client'
import { useNavigate, useParams } from '@faceup/router'
import { useComponentDidMount, usePromiseSuspense } from '@faceup/ui'
import { type PropsWithChildren, Suspense, useContext, useEffect } from 'react'
import { graphql } from '../__generated__'
import { FullScreenLoader } from '../Components/FullScreenLoader'
import { LayoutContext } from '../Contexts/LayoutContext'
import { UserContext } from '../Contexts/UserContext'
import useRegion from '../utils/useRegion'

const query = graphql(`
  query PublicSurveyByShortIdQuery($shortId: GraphQLString!) {
    publicSurveyByShortId(shortId: $shortId) {
      id
      sourceType
      institution {
        id
      }
    }
  }
`)

const _AwaitSurvey = ({ children }: PropsWithChildren) => {
  const { shortId } = useParams<'shortId'>() as { shortId: string }
  const { setReportSource, reportSource } = useContext(UserContext)
  const navigate = useNavigate()
  const { discoverByShortlink } = useRegion()
  const discovery = usePromiseSuspense(discoverByShortlink, [shortId])
  const { client } = useContext(LayoutContext)
  const isApp = client === 'app'

  const { data } = useSuspenseQuery(query, {
    variables: {
      shortId,
    },
  })

  useComponentDidMount(() => {
    if (isApp) {
      if (!reportSource) {
        navigate(routes => routes.home())
      }
      return
    }
    if (!discovery) {
      console.error('No discovery')
      navigate(routes => routes.home())
      return
    }
    if (!data.publicSurveyByShortId) {
      console.error('No publicSurveyByShortId')
      navigate(routes => routes.home())
      return
    }
    if (discovery.reportSourceId && discovery.type) {
      setReportSource({
        channelType: data.publicSurveyByShortId.sourceType,
        defaultFormId: discovery.reportSourceId,
        institutionId: data.publicSurveyByShortId.institution.id,
        type: discovery.type,
        shortLink: shortId,
      })
    }
  })

  return <>{children}</>
}

export const AwaitSurvey = ({ children }: PropsWithChildren) => {
  const { reportSource } = useContext(UserContext)
  const { shortId } = useParams<'shortId'>()
  const navigate = useNavigate()

  useEffect(() => {
    if (!reportSource && !shortId) {
      console.error('No shortId in AwaitSurvey')
      navigate(routes => routes.home())
      return
    }
  }, [shortId, navigate, reportSource])

  /*
   * When reportSource is set in `_AwaitSurvey`,
   * `AppRouter` is rerendered and suspense inside `_AwaitSurvey` goes into infinite loop
   * So once reportSource is set, we skip this initial configuration
   */
  if (reportSource) {
    return children
  }

  return (
    <Suspense fallback={<FullScreenLoader />}>
      <_AwaitSurvey>{children}</_AwaitSurvey>
    </Suspense>
  )
}
